import { createV3Queries } from "@query/utils/createV3Queries";
import { engagementsApi } from "@api";
import { QUERY_LIST_RESOURCE_EVENTS } from "../../query/resourceEvents/queries/useQueryListEvents";
import {
  QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID,
  QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL,
  QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE
} from "../../query/payeeEngagements/keys";
import {
  QUERY_REQUIREMENT_DEFINITIONS_LIST_ALL,
  QUERY_REQUIREMENT_DEFINITIONS_LIST_SIZE
} from "../../query/requirementDefinitions/keys";
import { QUERY_EXTENDED_REQUIREMENT_DEFINITIONS } from "../../query/requirementDefinitions/query/useQueryExtendedRequirementDefinitions";

/**
 * Engagement TYPE (template) registry — mirrors `EngagementsApi` 1:1. The
 * name-derived defaults already refresh the sibling engagement reads
 * (`getEngagement` / `listEngagements`) after every write, so the configs below
 * only add the **cross-resource** invalidations the legacy v1 hooks declared —
 * still on v1 caches during coexistence:
 *  - the resource-events activity feed shown on the engagement-type detail screen,
 *  - the payee-engagement lists (a template create/requirement change is reflected
 *    in the per-payee engagement widgets),
 *  - the requirement-definition lists (an engagement's requirement set feeds the
 *    definition's "used by" view).
 *
 * `awaitDependencies` on `getEngagement` reproduces the v1 behavior of refetching
 * the detail before the mutating modal resolves.
 *
 * TODO(v3): drop the payee-engagement / requirement-definition v1 keys once those
 * resources read v3.
 */
export const queriesEngagements = createV3Queries(
  "engagementsApi",
  engagementsApi,
  {
    createEngagement: () => ({
      dependencies: [
        QUERY_LIST_RESOURCE_EVENTS,
        QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID,
        QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE,
        QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL
      ]
    }),
    updateEngagement: queries => ({
      dependencies: [QUERY_LIST_RESOURCE_EVENTS],
      awaitDependencies: (_res, req) => [
        queries.getEngagement.getQueryKey({ engagementId: req.engagementId })
      ]
    }),
    archiveEngagement: queries => ({
      dependencies: [QUERY_LIST_RESOURCE_EVENTS],
      awaitDependencies: (_res, req) => [
        queries.getEngagement.getQueryKey({ engagementId: req.engagementId })
      ]
    }),
    restoreEngagement: queries => ({
      dependencies: [QUERY_LIST_RESOURCE_EVENTS],
      awaitDependencies: (_res, req) => [
        queries.getEngagement.getQueryKey({ engagementId: req.engagementId })
      ]
    }),
    deleteEngagement: () => ({
      dependencies: [QUERY_LIST_RESOURCE_EVENTS]
    }),
    addEngagementRequirement: queries => ({
      dependencies: [
        QUERY_LIST_RESOURCE_EVENTS,
        QUERY_REQUIREMENT_DEFINITIONS_LIST_ALL,
        QUERY_REQUIREMENT_DEFINITIONS_LIST_SIZE,
        QUERY_EXTENDED_REQUIREMENT_DEFINITIONS,
        QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID,
        QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE,
        QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL
      ],
      awaitDependencies: (_res, req) => [
        queries.getEngagement.getQueryKey({ engagementId: req.engagementId })
      ]
    }),
    removeEngagementRequirement: queries => ({
      dependencies: [QUERY_LIST_RESOURCE_EVENTS],
      awaitDependencies: (_res, req) => [
        queries.getEngagement.getQueryKey({ engagementId: req.engagementId })
      ]
    })
  }
);
